import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppSelector } from "@/store/store-hooks";
import { WaypointsColoringOptions } from "@/store/view-options/view-options-slice";
import {
  selectCompanyId,
  useLocalStorage,
} from "@faro-lotv/app-component-toolbox";
import { DistanceUnitTypes, validateEnumValue } from "@faro-lotv/foundation";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import {
  fetchDistanceUnitSetting,
  getDefaultUnit,
} from "./distance-unit-utils";

type SettingsContext = WaypointsColoringOptionCallbacks &
  UnitSetting & {
    /** The workspace waypoint color (hex) set in the dashboard, if any */
    waypointsCustomColor?: string;
  };

/** Context which provides the functionalities for managing settings */
export const SettingsContext = createContext<SettingsContext | undefined>(
  undefined,
);

/**
 * @returns a context useful for interacting with the settings of the app
 */
export function useSettingsContext(): SettingsContext {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("SettingsContext is not initialized.");
  }
  return context;
}

/**
 * @returns the current distance unit setting
 */
export function useDistanceUnit(): DistanceUnitTypes {
  // Do not throw if the context is not available, just return undefined
  // This allows components to use this hook without being wrapped in the provider
  // and handle the undefined case gracefully.
  const context = useContext(SettingsContext);
  return context ? context.distanceUnit : getDefaultUnit();
}

/**
 * @returns a provider component for the SettingsContext
 */
export function SettingsContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { waypointsColoring, setWaypointsColoring } =
    useWaypointsColoringOption();

  const waypointsCustomColor = useWaypointsCustomColor();

  const { distanceUnit, setDistanceUnit } = useDistanceUnitSetting();

  const context = useMemo(
    () => ({
      waypointsColoring,
      setWaypointsColoring,
      waypointsCustomColor,
      distanceUnit,
      setDistanceUnit,
    }),
    [
      waypointsColoring,
      setWaypointsColoring,
      waypointsCustomColor,
      distanceUnit,
      setDistanceUnit,
    ],
  );

  return <SettingsContext value={context}>{children}</SettingsContext>;
}

type WaypointsColoringOptionCallbacks = {
  /** The current value of the waypoints coloring option */
  waypointsColoring: WaypointsColoringOptions | undefined;
  /**
   * The setter to change the value of the waypoints coloring option
   *
   * @param option The new value of the waypoints coloring option
   * @param updateStorage If true, update the value in the browser local storage. Default is false.
   */
  setWaypointsColoring(
    option: WaypointsColoringOptions,
    updateStorage?: boolean,
  ): void;
};

/**
 * Initialize the waypoints coloring setting and provide the setter to interact with it
 *
 * @returns The waypoints coloring setting and its setter
 */
function useWaypointsColoringOption(): WaypointsColoringOptionCallbacks {
  const [waypointsColoringInStorage, setWaypointsColoringInStorage] =
    useLocalStorage<WaypointsColoringOptions>("waypointsColoring");

  const [waypointsColoring, setWaypointsColoringLocally] = useState(
    waypointsColoringInStorage,
  );

  const setWaypointsColoring = useCallback(
    (option: WaypointsColoringOptions, updateStorage: boolean = false) => {
      setWaypointsColoringLocally(option);
      if (updateStorage) {
        setWaypointsColoringInStorage(option);
      }
    },
    [setWaypointsColoringInStorage],
  );

  useEffect(() => {
    if (!validateEnumValue(waypointsColoring, WaypointsColoringOptions)) {
      // If the value in local storage is not valid, reset to undefined
      setWaypointsColoring(
        WaypointsColoringOptions.custom,
        waypointsColoringInStorage !== undefined,
      );
    }
  }, [waypointsColoring, setWaypointsColoring, waypointsColoringInStorage]);

  return {
    waypointsColoring,
    setWaypointsColoring,
  };
}

/**
 * Load the workspace waypoint color set in the dashboard
 *
 * @returns The workspace waypoint color (hex), or undefined if none is configured
 */
function useWaypointsCustomColor(): string | undefined {
  const { settingsApiClient } = useApiClientContext();
  const { isLoggedIn } = useAuthContext();
  const companyId = useAppSelector(selectCompanyId);

  const [waypointsCustomColor, setWaypointsCustomColor] = useState<string>();

  useEffect(() => {
    // Reset before fetching so a color from another workspace, or from a previous login, is not used
    setWaypointsCustomColor(undefined);

    if (!isLoggedIn || !companyId) return;

    let isActive = true;
    settingsApiClient.company
      .fetchCompanySettings(companyId, "waypoints_custom_color")
      .then((setting) => {
        if (isActive) setWaypointsCustomColor(setting.value);
      })
      // Fetch errors (e.g. no color configured): reset so the renderer falls back to the default color
      .catch(() => {
        if (isActive) setWaypointsCustomColor(undefined);
      });

    return () => {
      isActive = false;
    };
  }, [isLoggedIn, companyId, settingsApiClient]);

  return waypointsCustomColor;
}

type UnitSetting = {
  /** The current value of the distance unit type */
  distanceUnit: DistanceUnitTypes;

  /**
   * The setter to change the value of the distance unit type
   *
   * @param unitType The new value of the distance unit type
   */
  setDistanceUnit(unitType: DistanceUnitTypes): void;
};

/**
 * Initialize the unit setting and setter function
 *
 * @returns The distance unit setting and its setter
 */
function useDistanceUnitSetting(): UnitSetting {
  const { handleErrorWithToast } = useErrorHandlers();
  const [distanceUnit, setDistanceUnit] = useState<DistanceUnitTypes>(() =>
    getDefaultUnit(),
  );

  const [unitOfMeasure] = useLocalStorage<string>("unitOfMeasure");

  const { settingsApiClient } = useApiClientContext();
  useEffect(() => {
    fetchDistanceUnitSetting(settingsApiClient)
      .then(async (unitType) => {
        if (unitType === undefined) {
          if (unitOfMeasure === "us" || unitOfMeasure === "metric") {
            const newUnit =
              unitOfMeasure === "us"
                ? DistanceUnitTypes.feetFractionalInches
                : DistanceUnitTypes.meters;
            setDistanceUnit(newUnit);

            if (SettingsApiTypes.isValidUnitMeasurement(newUnit)) {
              const { modifiedAt } =
                await settingsApiClient.user.fetchUserSettings(
                  "unit_measurement",
                );
              await settingsApiClient.user.updateUserSettings(
                "unit_measurement",
                {
                  value: newUnit,
                  modifiedAt,
                },
              );
            }
          }
        } else {
          setDistanceUnit(unitType);
        }
      })
      .catch((error) =>
        handleErrorWithToast({
          title: "Fetching distance unit failed",
          error,
        }),
      );
  }, [handleErrorWithToast, settingsApiClient, unitOfMeasure]);

  return { distanceUnit, setDistanceUnit };
}
